import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import API from '../../../services/API'
import { SoftChip } from '../../kit/Chips'
import { formatPeriod } from '../../kit/helpers'
import { EMAIL_STAGES, RECIPIENT_ROLES } from './constants'

// Addresses are escaped apart from the "@", so an unusual one cannot break
// out of the link.
const mailtoHref = (draft) =>
  `mailto:${(draft.to ?? []).map((email) => encodeURIComponent(email).replace(/%40/g, '@')).join(',')}` +
  `?subject=${encodeURIComponent(draft.subject ?? '')}&body=${encodeURIComponent(draft.body ?? '')}`

const clipboardText = (draft) =>
  [`To: ${(draft.to ?? []).join(', ')}`, `Subject: ${draft.subject ?? ''}`, '', draft.body ?? ''].join('\n')

// Read-only email draft (advisory / quote / seasonal review) built by the
// server for one month. It only fetches and shows text: opening it, copying
// it or handing it to the email app sends nothing and records nothing -
// marking the email as sent is a separate, explicit action on the panel.
// `query` carries quote details typed but not yet recorded, for the preview.
const EmailDraftDialog = ({ open, tenantID, period, stage, query, onClose, onToast }) => {
  const [draft, setDraft] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open || !period || !stage) return undefined
    let cancelled = false
    setDraft(null)
    setError('')
    setLoading(true)
    API.get(`/admincrm/tenants/${tenantID}/commercial-usage/${period}/email-draft`, {
      params: { stage, ...(query ?? {}) },
    })
      .then((res) => {
        if (cancelled) return
        if (res.status === 200 && res.data) setDraft(res.data)
        else setError(res.data?.message || res.data?.error || 'Could not build the draft.')
      })
      .catch(() => {
        if (!cancelled) setError('Could not build the draft.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, tenantID, period, stage, query])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(clipboardText(draft))
      onToast?.('Email copied - nothing has been recorded.')
    } catch (e) {
      onToast?.('Could not copy automatically - select the text in the draft and copy it by hand.')
    }
  }

  const meta = EMAIL_STAGES[stage] ?? { label: 'Email', recordWith: 'the matching action' }
  const recipients = draft?.recipients ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {meta.label} draft - {formatPeriod(period, 'MMMM YYYY')}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} paddingTop={1}>
          <Alert severity="info">
            AmbuNet does not send this email. Opening or copying the draft records nothing - send
            it from your own mailbox, then use <b>{meta.recordWith}</b> to record that you did.
          </Alert>
          {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
          {error && <Alert severity="error">{error}</Alert>}
          {draft && (
            <>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontSize: '0.66rem',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  To
                </Typography>
                {recipients.length > 0 ? (
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    {recipients.map((recipient) => (
                      <SoftChip
                        key={recipient.email}
                        color="#268EBB"
                        label={`${recipient.name ? `${recipient.name} · ` : ''}${recipient.email} (${RECIPIENT_ROLES[recipient.role] ?? recipient.role})`}
                        sx={{ maxWidth: '100%' }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No recipients were found (the account owner and site administrators could not
                    be read). Add the address in your email app.
                  </Typography>
                )}
              </Box>
              <TextField
                label="Subject"
                size="small"
                fullWidth
                value={draft.subject ?? ''}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Body"
                size="small"
                fullWidth
                multiline
                minRows={10}
                maxRows={20}
                value={draft.body ?? ''}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { fontSize: '0.85rem', lineHeight: 1.5 } }}
              />
              <Typography variant="caption" color="text.secondary">
                Check the draft before sending. If your email app opens with the text cut short,
                use Copy email instead.
                {stage === 'quote' && !query
                  ? ' Details shown in [square brackets] are filled in when you preview the draft from Record quote sent.'
                  : ''}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button startIcon={<ContentCopyIcon />} onClick={copy} disabled={!draft}>
          Copy email
        </Button>
        <Button
          variant="contained"
          component="a"
          href={draft ? mailtoHref(draft) : undefined}
          endIcon={<OpenInNewIcon />}
          disabled={!draft}
        >
          Open in email app
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailDraftDialog
